import type { HolderListItem } from "./types";

/**
 * The holder tree, flattened for a `<select>`.
 *
 * `GET /holders` sends every holder in one list (the tree is small), and a native
 * select is the most dependable control there is — keyboard, screen reader and
 * mobile all handled. Depth is carried as an em-space indent in the label, so the
 * flat list still reads as the tree it is.
 */

/**
 * Express, the system group every card defaults to. Fixed by migration `0039`, so
 * the panel can preselect it before the holder list has loaded.
 */
export const EXPRESS_HOLDER_ID = "00000000-0000-4000-8000-000000000001";

export interface HolderOption {
  readonly id: string;
  readonly label: string;
  readonly name: string;
  readonly type: HolderListItem["type"];
  readonly depth: number;
  readonly isSystem: boolean;
}

const INDENT = " ";

/** Express first, then every root and its subtree, depth-first in name order. */
export function holderOptions(rows: readonly HolderListItem[]): readonly HolderOption[] {
  const ids = new Set(rows.map((row) => row.id));
  const children = new Map<string | null, HolderListItem[]>();
  for (const row of rows) {
    // A parent missing from this fetch (deleted between two reads) would orphan the
    // row and hide it; shown at the top level, it stays reachable.
    const parent = row.parentHolderId !== null && ids.has(row.parentHolderId) ? row.parentHolderId : null;
    children.set(parent, [...(children.get(parent) ?? []), row]);
  }

  const out: HolderOption[] = [];
  const visit = (parent: string | null, depth: number) => {
    const level = [...(children.get(parent) ?? [])].sort(byExpressThenName);
    for (const row of level) {
      out.push({
        id: row.id,
        label: `${INDENT.repeat(depth)}${row.name}`,
        name: row.name,
        type: row.type,
        depth,
        isSystem: row.isSystem,
      });
      visit(row.id, depth + 1);
    }
  };
  visit(null, 0);
  return out;
}

/** The holder and every holder under it. */
export function subtreeIds(rows: readonly HolderListItem[], rootId: string): ReadonlySet<string> {
  const found = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const row of rows) {
      if (row.parentHolderId !== null && found.has(row.parentHolderId) && !found.has(row.id)) {
        found.add(row.id);
        grew = true;
      }
    }
  }
  return found;
}

/**
 * Where a holder may be moved: any group outside its own subtree. The server is
 * the gate (it also refuses a move past eight levels); this only keeps the
 * impossible choices out of the list.
 */
export function moveTargets(rows: readonly HolderListItem[], holderId: string): readonly HolderOption[] {
  const own = subtreeIds(rows, holderId);
  return holderOptions(rows).filter((option) => option.type === "group" && !own.has(option.id));
}

function byExpressThenName(a: HolderListItem, b: HolderListItem): number {
  if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
  return a.name.localeCompare(b.name);
}

/**
 * The groups above a holder, outermost first. Empty at the top level or for a
 * holder this list does not have. Stops at a repeat rather than looping, though
 * the repository refuses a cycle.
 */
export function ancestry(
  rows: readonly HolderListItem[],
  holderId: string,
): readonly HolderListItem[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const seen = new Set([holderId]);
  const path: HolderListItem[] = [];
  let parentId = byId.get(holderId)?.parentHolderId ?? null;
  while (parentId !== null && !seen.has(parentId)) {
    const parent = byId.get(parentId);
    if (parent === undefined) break;
    seen.add(parentId);
    path.unshift(parent);
    parentId = parent.parentHolderId;
  }
  return path;
}
